import React, { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { UnorderedListOutlined } from '@ant-design/icons';
import { Button, Form, notification, Typography } from 'antd';
import type { FormInstance } from 'antd/es/form';

// The eventTypes constants import this component, so take the type names from commons.
import { TYPE_OF_COMPONENTS as COMPONENT_TYPES } from '@src/constants/commons';
import { clearOptionListsCache } from '@src/hooks/useOptionLists';
import { OptionListMapper } from '@src/models/mappers/optionList.mapper';
import { useSelectFieldContext } from '@src/packages/eventTypes/context/SelectFieldContext';
import { OPTION_LIST_NAME_MAX_LENGTH } from '@src/packages/optionLists/constants';
import OptionListService from '@src/services/OptionListService';

interface ConvertToOptionListProps {
  id: number;
  form: FormInstance;
  /** Inline options a selector may hold; at the cap the action is highlighted. */
  limitOptions: number;
}

/** Same naming as the GUA-199 migration: "<form> · <field>". */
export const convertedListName = (formName: string | undefined, fieldName: string | undefined, fallback: string) =>
  [formName?.trim(), fieldName?.trim()].filter(Boolean).join(' · ').slice(0, OPTION_LIST_NAME_MAX_LENGTH) || fallback;

/**
 * "Save as list": turns a selector's inline options into a saved option list and points the field
 * at it, so it can grow past the inline cap and be reused by other forms. Offered to the users who
 * may create lists; the list belongs to the organization or to the form's division (see
 * `useOptionListOwnership`, provided through `SelectFieldContext`).
 */
const ConvertToOptionList: React.FC<ConvertToOptionListProps> = ({ id, form, limitOptions }) => {
  const intl = useIntl();
  const { organizationId, formName, onDirty, ownerDivisionId, canCreateList } = useSelectFieldContext();
  const service = useMemo(() => new OptionListService(), []);
  const [saving, setSaving] = useState(false);
  // `preserve`: read the store even where no input is mounted for that path.
  const options = (Form.useWatch(['components', id, 'componentProps', 'options'], { form, preserve: true }) ??
    []) as unknown[];
  const fieldName = Form.useWatch(['components', id, 'label', 'name'], { form, preserve: true }) as
    | string
    | undefined;

  const labels = OptionListMapper.uniqueLabels(options.map(option => String(option ?? '')));
  if (!organizationId || !canCreateList || labels.length === 0) return null;
  const atLimit = options.length >= limitOptions;

  const convert = async () => {
    setSaving(true);
    try {
      const name = convertedListName(
        formName,
        fieldName,
        intl.formatMessage({ id: 'eventTypes.component.componentProps.convertToList.defaultName' }),
      );
      const listId = await service.create(organizationId, name, labels, ownerDivisionId);
      clearOptionListsCache(organizationId);
      form.setFieldValue(['components', id, 'type'], COMPONENT_TYPES.LIST_SELECT);
      form.setFieldValue(['components', id, 'componentProps'], { optionListId: listId });
      onDirty?.();
      notification.success({
        message: intl.formatMessage({ id: 'eventTypes.component.componentProps.convertToList.done' }, { name }),
      });
    } catch {
      notification.error({ message: intl.formatMessage({ id: 'general.save.error.message' }) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {atLimit && (
        <Typography.Paragraph type="secondary">
          {intl.formatMessage({ id: 'eventTypes.component.componentProps.convertToList.atLimit' }, { max: limitOptions })}
        </Typography.Paragraph>
      )}
      <Button icon={<UnorderedListOutlined />} loading={saving} onClick={convert} type={atLimit ? 'primary' : 'link'}>
        {intl.formatMessage({ id: 'eventTypes.component.componentProps.convertToList' })}
      </Button>
    </div>
  );
};

export default ConvertToOptionList;
